/**
 * desayuno.ts — Categoría "Desayuno" (10 productos demo con personalización).
 *
 * Incluye caldos individuales, desayuno completo, huevos al gusto, moñona
 * y platos típicos colombianos. Modificadores en _modifiersDesayuno.ts.
 */
import { buildProduct } from "./_factory";
import { DESAYUNO_MODIFIERS_BY_INDEX } from "./_modifiersDesayuno";
import { DESAYUNO_GALERIA_BY_INDEX } from "./_desayunoImages";
import {
  HORARIO_DESAYUNO,
  withDemoCajaLibreIfModifiers,
} from "./_restaurantDemo.constants";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_desay",
  skuPrefix: "DESAY",
  categoria: "Desayuno",
} as const;

const breakfast = (
  input: Omit<Parameters<typeof buildProduct>[0], keyof typeof CAT>,
) =>
  buildProduct({
    ...CAT,
    disponibilidadHoraria: HORARIO_DESAYUNO,
    ...withDemoCajaLibreIfModifiers(input),
  });

const recetaCaldoBase = (proteina: string, gramos: number) => [
  {
    nombre: proteina,
    cantidadCompra: 1000,
    unidadCompra: "g",
    costoCompra: 14500,
    cantidadUso: gramos,
    unidadUso: "g",
  },
  {
    nombre: "Papa Pastusa 1kg",
    cantidadCompra: 1000,
    unidadCompra: "g",
    costoCompra: 2400,
    cantidadUso: 120,
    unidadUso: "g",
  },
  {
    nombre: "Plátano Verde",
    cantidadCompra: 1,
    unidadCompra: "unidad",
    costoCompra: 1200,
    cantidadUso: 0.5,
    unidadUso: "unidad",
  },
  {
    nombre: "Cebolla Cabezona 1kg",
    cantidadCompra: 1000,
    unidadCompra: "g",
    costoCompra: 2600,
    cantidadUso: 40,
    unidadUso: "g",
  },
  {
    nombre: "Cilantro Fresco",
    cantidadCompra: 1,
    unidadCompra: "unidad",
    costoCompra: 900,
    cantidadUso: 0.2,
    unidadUso: "unidad",
  },
];

export const cat_desay_products: Producto[] = [
  breakfast({
    index: 0,
    nombre: "Caldo de Costilla",
    marca: "Casa",
    costo: 0,
    precio: 8000,
    stock: 35,
    stockMinimo: 10,
    stockMaximo: 70,
    unidad: "plato",
    ubicacionFisica: "Cocina, olla del día",
    proveedor: "Producción propia",
    tiempoEntrega: "10 min",
    descripcionLarga:
      "Caldo casero de costilla de res con papa, plátano, yuca y cilantro. Servido caliente, ideal para arrancar el día con fuerza.\n\nPara llevar +$1.000 (icópor).",
    especificaciones: [
      { clave: "Tipo", valor: "Caldo tradicional" },
      { clave: "Porción", valor: "1 plato abundante" },
    ],
    imageKeywords: ["beef-soup", "colombian-broth", "breakfast-soup"],
    color: "bg-amber-700",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: recetaCaldoBase("Costilla de Res 1kg", 180),
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[0],
    galeria: DESAYUNO_GALERIA_BY_INDEX[0],
  }),
  breakfast({
    index: 1,
    nombre: "Caldo de Pescado",
    marca: "Casa",
    costo: 0,
    precio: 8000,
    stock: 32,
    stockMinimo: 8,
    stockMaximo: 65,
    unidad: "plato",
    ubicacionFisica: "Cocina, olla del día",
    proveedor: "Producción propia",
    tiempoEntrega: "10 min",
    descripcionLarga:
      "Caldo de pescado fresco con verduras, plátano y cilantro. Ligero, nutritivo y típico de la costa en versión desayuno.\n\nPara llevar +$1.000 (icópor).",
    especificaciones: [
      { clave: "Proteína", valor: "Pescado del día" },
      { clave: "Tiempo prep", valor: "10 min" },
    ],
    imageKeywords: ["fish-soup", "caldo-pescado", "seafood-broth"],
    color: "bg-sky-600",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: recetaCaldoBase("Filete de Tilapia 500g", 150),
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[1],
    galeria: DESAYUNO_GALERIA_BY_INDEX[1],
  }),
  breakfast({
    index: 2,
    nombre: "Caldo de Pajarilla",
    marca: "Casa",
    costo: 0,
    precio: 10000,
    stock: 28,
    stockMinimo: 8,
    stockMaximo: 55,
    unidad: "plato",
    ubicacionFisica: "Cocina, olla del día",
    proveedor: "Producción propia",
    tiempoEntrega: "12 min",
    descripcionLarga:
      "Caldo especial de pajarilla (menudencias de res) cocinado a fuego lento. Sabor profundo y contundente, favorito de madrugadores.\n\nPara llevar +$1.000 (empaque · total $11.000).",
    especificaciones: [
      { clave: "Especialidad", valor: "Pajarilla" },
      { clave: "Precio", valor: "$10.000" },
    ],
    imageKeywords: ["beef-broth", "traditional-soup", "colombian-breakfast"],
    color: "bg-red-800",
    shape: "circle",
    gastoMargen: "estandar",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: recetaCaldoBase("Menudencias de Res 1kg", 200),
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[2],
    galeria: DESAYUNO_GALERIA_BY_INDEX[2],
  }),
  breakfast({
    index: 3,
    nombre: "Caldo de Pata",
    marca: "Casa",
    costo: 0,
    precio: 9000,
    stock: 30,
    stockMinimo: 8,
    stockMaximo: 60,
    unidad: "plato",
    ubicacionFisica: "Cocina, olla del día",
    proveedor: "Producción propia",
    tiempoEntrega: "12 min",
    descripcionLarga:
      "Caldo de pata de res cocido por horas hasta quedar meloso. Con papa, plátano y hierbas. Reconfortante y llenador.\n\nPara llevar +$1.000 (empaque · total $10.000).",
    especificaciones: [
      { clave: "Cocción", valor: "Lenta 3h" },
      { clave: "Precio", valor: "$9.000" },
    ],
    imageKeywords: ["cow-foot-soup", "broth", "breakfast-bowl"],
    color: "bg-amber-800",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: recetaCaldoBase("Pata de Res 1kg", 220),
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[3],
    galeria: DESAYUNO_GALERIA_BY_INDEX[3],
  }),
  breakfast({
    index: 4,
    nombre: "Desayuno Completo",
    marca: "Casa",
    costo: 0,
    precio: 12000,
    stock: 45,
    stockMinimo: 12,
    stockMaximo: 90,
    unidad: "plato",
    ubicacionFisica: "Cocina, barra desayunos",
    proveedor: "Producción propia",
    tiempoEntrega: "15 min",
    descripcionLarga:
      "Desayuno corriente completo: elige caldo (costilla o pescado incluidos · pata +$1.000 · pajarilla +$2.000), huevos al gusto, arroz o pan, bebida y cubiertos.\n\nPrecio base $12.000 en el local. Para llevar +$1.000 (icópor). Personalización desde tienda o caja.",
    especificaciones: [
      { clave: "Precio base", valor: "$12.000 (en el local)" },
      { clave: "Incluye", valor: "Caldo + huevos + acompañante + bebida" },
    ],
    imageKeywords: ["colombian-breakfast", "eggs-rice", "full-breakfast"],
    color: "bg-orange-500",
    shape: "circle",
    gastoMargen: "estandar",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Huevos AA Kikes x30",
        cantidadCompra: 30,
        unidadCompra: "unidad",
        costoCompra: 14500,
        cantidadUso: 2,
        unidadUso: "unidad",
      },
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 80,
        unidadUso: "g",
      },
      {
        nombre: "Pan Blanco Tajado Bimbo 680g",
        cantidadCompra: 22,
        unidadCompra: "unidad",
        costoCompra: 6500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Costilla de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 18200,
        cantidadUso: 120,
        unidadUso: "g",
      },
    ],
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[4],
    galeria: DESAYUNO_GALERIA_BY_INDEX[4],
  }),
  breakfast({
    index: 5,
    nombre: "Huevos al Gusto",
    marca: "Casa",
    costo: 0,
    precio: 8000,
    stock: 50,
    stockMinimo: 15,
    stockMaximo: 100,
    unidad: "plato",
    ubicacionFisica: "Cocina, plancha desayunos",
    proveedor: "Producción propia",
    tiempoEntrega: "8 min",
    descripcionLarga:
      "Huevos pericos, revueltos, tortilla o fritos — con arroz con huevo o pan con huevo (1 pan), bebida caliente o fría y cubiertos a tu elección.\n\nPara llevar +$1.000 (icópor).",
    especificaciones: [
      { clave: "Precio", valor: "$8.000" },
      { clave: "Tiempo prep", valor: "8 min" },
    ],
    imageKeywords: ["fried-eggs", "scrambled-eggs", "breakfast-eggs"],
    color: "bg-yellow-500",
    shape: "circle",
    gastoMargen: "estandar",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Huevos AA Kikes x30",
        cantidadCompra: 30,
        unidadCompra: "unidad",
        costoCompra: 14500,
        cantidadUso: 2,
        unidadUso: "unidad",
      },
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 70,
        unidadUso: "g",
      },
      {
        nombre: "Pan Blanco Tajado Bimbo 680g",
        cantidadCompra: 22,
        unidadCompra: "unidad",
        costoCompra: 6500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
    ],
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[5],
    galeria: DESAYUNO_GALERIA_BY_INDEX[5],
  }),
  breakfast({
    index: 6,
    nombre: "Moñona",
    marca: "Casa",
    costo: 0,
    precio: 14000,
    stock: 35,
    stockMinimo: 10,
    stockMaximo: 70,
    unidad: "plato",
    ubicacionFisica: "Cocina, parrilla desayunos",
    proveedor: "Producción propia",
    tiempoEntrega: "15 min",
    descripcionLarga:
      "Moñona tradicional: huevos al gusto, arroz o pan, proteína a la plancha (res, cerdo, pechuga o bistec), bebida y cubiertos. Plato contundente del morning menu.\n\nPara llevar +$1.000 (icópor).",
    especificaciones: [
      { clave: "Precio", valor: "$14.000" },
      { clave: "Proteína", valor: "A elegir" },
    ],
    imageKeywords: ["grilled-meat-eggs", "colombian-breakfast", "steak-eggs"],
    color: "bg-red-700",
    shape: "square",
    gastoMargen: "ambicioso",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Huevos AA Kikes x30",
        cantidadCompra: 30,
        unidadCompra: "unidad",
        costoCompra: 14500,
        cantidadUso: 2,
        unidadUso: "unidad",
      },
      {
        nombre: "Carne Molida de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 19500,
        cantidadUso: 120,
        unidadUso: "g",
      },
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 90,
        unidadUso: "g",
      },
    ],
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[6],
    galeria: DESAYUNO_GALERIA_BY_INDEX[6],
  }),
  breakfast({
    index: 7,
    nombre: "Arepa con Huevo y Queso",
    marca: "Casa",
    costo: 0,
    precio: 8500,
    stock: 40,
    stockMinimo: 12,
    stockMaximo: 80,
    unidad: "porción",
    ubicacionFisica: "Cocina, freidora desayunos",
    proveedor: "Producción propia",
    tiempoEntrega: "6 min",
    descripcionLarga:
      "Arepa de maíz rellena de huevo entero y queso campesino derretido. Crujiente por fuera, suave por dentro. Clásico costeño de media mañana.",
    especificaciones: [
      { clave: "Origen", valor: "Receta costeña" },
      { clave: "Tiempo prep", valor: "6 min" },
    ],
    imageKeywords: ["arepa-huevo", "colombian-arepa", "street-food"],
    color: "bg-amber-500",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Harina de Maíz PAN 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 5200,
        cantidadUso: 80,
        unidadUso: "g",
      },
      {
        nombre: "Huevos AA Kikes x30",
        cantidadCompra: 30,
        unidadCompra: "unidad",
        costoCompra: 14500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Queso Campesino Colanta 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 9800,
        cantidadUso: 40,
        unidadUso: "g",
      },
    ],
    galeria: DESAYUNO_GALERIA_BY_INDEX[7],
  }),
  breakfast({
    index: 8,
    nombre: "Chocolatada con Almojábana",
    marca: "Casa",
    costo: 0,
    precio: 7500,
    stock: 45,
    stockMinimo: 15,
    stockMaximo: 90,
    unidad: "porción",
    ubicacionFisica: "Barra caliente, desayunos",
    proveedor: "Producción propia",
    tiempoEntrega: "5 min",
    descripcionLarga:
      "Chocolate caliente espeso con leche entera, acompañado de almojábana recién horneada. Combo dulce y reconfortante para empezar el día.",
    especificaciones: [
      { clave: "Bebida", valor: "Chocolate caliente" },
      { clave: "Acompañante", valor: "1 almojábana" },
    ],
    imageKeywords: ["hot-chocolate", "almojabana", "colombian-breakfast"],
    color: "bg-amber-900",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Leche Entera Alquería 1L",
        cantidadCompra: 1000,
        unidadCompra: "ml",
        costoCompra: 4200,
        cantidadUso: 250,
        unidadUso: "ml",
      },
      {
        nombre: "Chocolate Corona 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 8500,
        cantidadUso: 30,
        unidadUso: "g",
      },
      {
        nombre: "Almojábana Tradicional x6",
        cantidadCompra: 6,
        unidadCompra: "unidad",
        costoCompra: 4800,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
    ],
    galeria: DESAYUNO_GALERIA_BY_INDEX[8],
  }),
  breakfast({
    index: 9,
    nombre: "Tamal Santafereño",
    marca: "Casa",
    costo: 0,
    precio: 12000,
    stock: 30,
    stockMinimo: 8,
    stockMaximo: 60,
    unidad: "porción",
    ubicacionFisica: "Cocina, vapor desayunos",
    proveedor: "Producción propia",
    tiempoEntrega: "10 min",
    descripcionLarga:
      "Tamal bogotano envuelto en hoja de plátano: pollo, cerdo, arroz, papa y guiso. Servido con bebida a elección. Personaliza cubiertos al pedir.",
    especificaciones: [
      { clave: "Origen", valor: "Receta santafereña" },
      { clave: "Precio", valor: "$12.000" },
    ],
    imageKeywords: ["tamal", "colombian-tamal", "breakfast-wrap"],
    color: "bg-green-700",
    shape: "square",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 100,
        unidadUso: "g",
      },
      {
        nombre: "Pechuga de Pollo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 14500,
        cantidadUso: 80,
        unidadUso: "g",
      },
      {
        nombre: "Costilla de Cerdo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 18200,
        cantidadUso: 60,
        unidadUso: "g",
      },
      {
        nombre: "Plátano Verde",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 1200,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
    ],
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[9],
    galeria: DESAYUNO_GALERIA_BY_INDEX[9],
  }),
  breakfast({
    index: 10,
    nombre: "Consomé de Caldo",
    marca: "Casa",
    costo: 0,
    precio: 4000,
    stock: 40,
    stockMinimo: 10,
    stockMaximo: 80,
    unidad: "plato",
    ubicacionFisica: "Cocina, olla del día",
    proveedor: "Producción propia",
    tiempoEntrega: "8 min",
    descripcionLarga:
      "Consomé de caldo ligero y caliente, ideal para abrir el apetito. Versión económica del caldo de la casa.\n\nPara llevar +$1.000 (icópor).",
    especificaciones: [
      { clave: "Tipo", valor: "Consomé ligero" },
      { clave: "Porción", valor: "1 plato" },
    ],
    imageKeywords: ["consomme", "beef-broth", "light-soup"],
    color: "bg-amber-600",
    shape: "circle",
    gastoMargen: "conservador",
    esReceta: false,
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[10],
    galeria: DESAYUNO_GALERIA_BY_INDEX[10],
  }),
  breakfast({
    index: 11,
    nombre: "Changua",
    marca: "Casa",
    costo: 0,
    precio: 8000,
    stock: 30,
    stockMinimo: 8,
    stockMaximo: 60,
    unidad: "plato",
    ubicacionFisica: "Cocina, olla del día",
    proveedor: "Producción propia",
    tiempoEntrega: "10 min",
    descripcionLarga:
      "Changua bogotana: caldo de leche con huevo pochado, cebolla, cilantro y pan. Clásico despertador santafereño.\n\nPara llevar +$1.000 (icópor).",
    especificaciones: [
      { clave: "Origen", valor: "Tradición bogotana" },
      { clave: "Incluye", valor: "Huevo + pan + cilantro" },
    ],
    imageKeywords: ["changua", "milk-soup", "colombian-breakfast"],
    color: "bg-sky-700",
    shape: "circle",
    gastoMargen: "estandar",
    destacado: true,
    esReceta: false,
    gruposModificadores: DESAYUNO_MODIFIERS_BY_INDEX[11],
    galeria: DESAYUNO_GALERIA_BY_INDEX[11],
  }),
];
